import { DeleteOutline } from '@mui/icons-material';
import { Card, Divider, Grid, IconButton, Typography, Box } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import AddMoreButton from '../../../FormFields/AddMoreButton';
import FormAutocomplete from '../../../FormFields/FormAutocomplete';
import FormButtonOptions from '../../../FormFields/FormButtonOptions';
import FormYearPickerField from '../../../FormFields/FormYearPickerField';
import FormFileDrop from '../../../FormFields/FormFileDrop';
import FormTextField from '../../../FormFields/FormTextField';
import FormTitle from '../../../FormFields/FormTitle';
import { formCardStyle, groupedFieldsStyle } from '../../../TransitionStyles';
import FieldContainer from '../../../FormFields/FieldContainer';
import { TRANSPORTATION } from '../../../formQuestionsEnum';
import { OWNERSHIP_TYPES } from '../../../optionEnums';
import { FILE_TYPES } from '../../../formFileTypesEnum';
import { validateVIN } from '../../../ValidateFunctions';

VehiclesForm.propTypes = {
  permissions: PropTypes.object,
};

export default function VehiclesForm({ permissions }) {
  const { control, setValue } = useFormContext();
  const [focusedField, setFocusedField] = useState(null);

  const {
    data: { isCanceled, ownershipTypesList, vehicleDepartmentsTypesList },
  } = useSelector((state) => state.transitions);

  const baseFieldName = TRANSPORTATION.VEHICLE_LIST.fieldName;
  const isEditable = permissions?.[TRANSPORTATION.VEHICLE_LIST.id];
  const disabled = !isEditable || isCanceled;

  const { fields, append } = useFieldArray({
    control,
    name: baseFieldName,
    keyName: '_id',
  });

  const watchedValues = useWatch({ control, name: baseFieldName }) || [];

  const visibleRows = fields.map((field, idx) => ({ field, idx })).filter(({ idx }) => !watchedValues[idx]?.deleteFlag);

  const handleDelete = (idx) => {
    setValue(`${baseFieldName}[${idx}].deleteFlag`, true, { shouldDirty: true });
  };

  return (
    TRANSPORTATION.VEHICLE_LIST.id in permissions && (
      <Card sx={formCardStyle}>
        <Typography sx={{ mb: 1 }} align="center" variant="body1" color="info.main">
          Vehicles
        </Typography>
        <Divider sx={{ mb: 1, mt: 2, borderWidth: 1 }} />
        <Grid item xs={12} sx={{ mb: 1 }}>
          <FormTitle fieldAttributes={{ questionTitle: 'Vehicle Information', fieldType: 'title' }} />
        </Grid>
        {visibleRows.map(({ field, idx }) => (
          <Grid container item xs={12} wrap="nowrap" key={field._id} alignItems="center" sx={{ mb: 1 }}>
            <Grid item xs={visibleRows.length > 1 ? 11.5 : 12}>
              <Box sx={{ backgroundColor: '#f1f3f4cc', ...groupedFieldsStyle }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={3}>
                    <FieldContainer>
                      <FormTitle fieldAttributes={{ questionTitle: 'Make' }} />
                      <FormTextField
                        fieldName={`${baseFieldName}[${idx}].make`}
                        fieldAttributes={{ isEditable }}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                      />
                    </FieldContainer>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <FieldContainer>
                      <FormTitle fieldAttributes={{ questionTitle: 'Model' }} />
                      <FormTextField
                        fieldName={`${baseFieldName}[${idx}].model`}
                        fieldAttributes={{ isEditable }}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                      />
                    </FieldContainer>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FieldContainer>
                      <FormTitle fieldAttributes={{ questionTitle: 'VIN' }} />
                      <FormTextField
                        fieldName={`${baseFieldName}[${idx}].vin`}
                        fieldAttributes={{
                          isEditable,
                          validation: {
                            validate: validateVIN,
                          },
                        }}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                      />
                    </FieldContainer>
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <FieldContainer>
                      <FormTitle fieldAttributes={{ questionTitle: 'Year' }} />
                      <FormYearPickerField
                        fieldName={`${baseFieldName}[${idx}].year`}
                        fieldAttributes={{ isEditable }}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                      />
                    </FieldContainer>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FieldContainer>
                      <FormTitle fieldAttributes={{ questionTitle: 'Mileage' }} />
                      <FormTextField
                        fieldName={`${baseFieldName}[${idx}].mileage`}
                        fieldAttributes={{ isEditable }}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                      />
                    </FieldContainer>
                  </Grid>

                  <Grid item xs={12} md>
                    <FieldContainer>
                      <FormTitle fieldAttributes={{ questionTitle: 'Department' }} />
                      <FormAutocomplete
                        fieldName={`${baseFieldName}[${idx}].vehicleDepartmentId`}
                        fieldAttributes={{ isEditable }}
                        options={vehicleDepartmentsTypesList}
                      />
                    </FieldContainer>
                  </Grid>

                  <Grid item xs={12} md="auto">
                    <FieldContainer>
                      <FormTitle fieldAttributes={{ questionTitle: 'Is the vehicle owned or leased?' }} />
                      <FormButtonOptions
                        fieldName={`${baseFieldName}[${idx}].ownershipTypeId`}
                        fieldAttributes={{
                          isEditable,
                          options: ownershipTypesList.filter(
                            (x) => x.value === OWNERSHIP_TYPES.LEASED || x.value === OWNERSHIP_TYPES.OWNED
                          ),
                        }}
                      />
                    </FieldContainer>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FieldContainer>
                      <FormTitle fieldAttributes={{ questionTitle: 'Insurance', fieldType: 'file' }} />
                      <FormFileDrop
                        fieldName={`${baseFieldName}[${idx}].insuranceFiles`}
                        fieldAttributes={{
                          isEditable,
                          fileTypeId: FILE_TYPES.VEHICLE_INSURANCE,
                        }}
                      />
                    </FieldContainer>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FieldContainer>
                      <FormTitle fieldAttributes={{ questionTitle: 'Registration', fieldType: 'file' }} />
                      <FormFileDrop
                        fieldName={`${baseFieldName}[${idx}].registrationFiles`}
                        fieldAttributes={{
                          isEditable,
                          fileTypeId: FILE_TYPES.VEHILCE_REGISTRATION,
                        }}
                      />
                    </FieldContainer>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            {visibleRows.length > 1 && (
              <Grid item xs={0.5}>
                <IconButton onClick={() => handleDelete(idx)} disabled={disabled} color="error">
                  <DeleteOutline />
                </IconButton>
              </Grid>
            )}
          </Grid>
        ))}

        <Grid item xs={12}>
          <AddMoreButton
            buttonLabel="Add more"
            handleAddMoreClick={() =>
              append({
                id: 0,
                make: null,
                model: null,
                year: null,
                vin: null,
                insuranceFile: [],
                registrationFile: [],
                mileage: null,
                vehicleDepartmentId: null,
                ownershipTypeId: null,
              })
            }
            disabled={disabled}
          />
        </Grid>
      </Card>
    )
  );
}
